/**
 * Dependencies.
 */
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import PackageModel from '@/api/package/package.model';
import { servicePointOrderService } from '@/api/service-point/order/order.service';
import OrderSteps from '@/modules/service-point/components/order/order-steps/order-steps.component.vue';

/**
 * Component.
 */
@Component({
  components: { 'order-steps': OrderSteps },
  name: 'deliver-order-connfirm-scene'
})
export default class extends Vue {
  /**
   * Property `isLoadingData`.
   */
  isLoadingData = false;

  /**
   * Property `phone`.
   */
  phone = null;

  /**
   * Property `email`.
   */
  email = null;

  /**
   * Property `identityNumber`.
   */
  identityNumber = null;

  /**
   * Property `name`.
   */
  name = null;

  /**
   * Property `deliveryOptions`.
   */
  deliveryOptions = [
    {
      icon: 'mdi-phone-outline',
      name: this.i18n('SERVICE_POINT.LABELS.PHONE'),
      type: 'PHONE_NUMBER'
    },
    {
      icon: 'mdi-email-outline',
      name: this.i18n('SERVICE_POINT.LABELS.EMAIL'),
      type: 'EMAIL'
    },
    {
      icon: 'mdi-card-account-details-outline',
      name: this.i18n('SERVICE_POINT.LABELS.IDENTITY_DOCUMENT'),
      type: 'IDENTITY_DOCUMENT'
    }
  ];

  /**
   * Property `selectedDeliveryOption`.
   */
  selectedDeliveryOption: {
    icon: string;
    name: string;
    type: string;
  } = this.deliveryOptions[1];

  /**
   * Property `servicePointOrderId`.
   */
  @Prop({ default: null, type: String })
  servicePointOrderId: string;

  /**
   * Property `trackingNumber`.
   */
  @Prop({ default: null, type: String })
  trackingNumber: string;

  /**
   * Property `packages`.
   */
  @Prop({ default: () => [], type: Array })
  packages: PackageModel[];

  /**
   * Getter `validatePhone`.
   */
  get validatePhone() {
    const regExp = /^[+]?\d+$/;

    return regExp.test(this.phone);
  }

  /**
   * Getter `validateEmail`.
   */
  get validateEmail() {
    const regExp = /\S+@\S+\.\S+/;

    return regExp.test(this.email);
  }

  /**
   * Getter `validateNumber`.
   */
  get validateNumber() {
    return !!this.identityNumber;
  }

  /**
   * Getter `hasValidData`.
   */
  get hasValidData() {
    let isValid =
      !!this.name && (!!this.phone || !!this.email || !!this.identityNumber);

    if (!!this.phone) {
      isValid = this.validatePhone;
    }

    if (!!this.email) {
      isValid = this.validateEmail;
    }

    if (!!this.identityNumber) {
      isValid = this.validateNumber;
    }

    if (!this.name) {
      isValid = false;
    }

    return isValid;
  }

  /**
   * Getter `notifyErrorSettings`.
   */
  get notifyErrorSettings() {
    return {
      backgroundColor: 'var(--pk-color-error-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-error)',
      iconText: 'warning',
      id: 'deviver-error',
      progressBarColor: 'var(--pk-color-error)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Lifecycle hook created.
   */
  created() {
    if (!this.servicePointOrderId) {
      this.$router.push({
        name: 'ServicePointHome'
      });
    }
  }

  /**
   * handle confirm receive.
   */
  async handleConfirmReceive() {
    const { type: deliverType } = this.selectedDeliveryOption || {};

    try {
      this.isLoadingData = true;

      let data: {
        deliverType?: string;
        deliveredTo?: string | { code: string; name: string };
        proofCode?: string;
        servicePointOrderId: number;
      } = { servicePointOrderId: Number(this.servicePointOrderId) };

      switch (deliverType) {
        case 'PHONE_NUMBER':
          data = {
            ...data,
            deliveredTo: this.name,
            proofCode: String(this.phone)
          };
          break;

        case 'EMAIL':
          data = {
            ...data,
            deliveredTo: this.name,
            proofCode: String(this.email)
          };
          break;

        case 'IDENTITY_DOCUMENT':
          data = {
            ...data,
            deliveredTo: JSON.stringify({
              code: String(this.identityNumber),
              name: String(this.name)
            })
          };
          break;
      }

      await servicePointOrderService.deliverWithOutPin({ data, deliverType });

      this.$router.push({
        name: 'ServicePointDeliverOrderWithoutPinStatus',
        params: { servicePointOrderId: this.servicePointOrderId }
      });
    } catch (error) {
      const { raw } = error || {};
      const { code } = raw || {};

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({ error, method: 'handleGetDeliveries' });
      }

      switch (code) {
        case 3421:
          this.$kit.notify.show(
            this.i18n('SERVICE_POINT.LABELS.ERROR'),
            this.i18n(
              'SERVICE_POINT.ERRORS.ORDER_DELIVER_INVALID_CODE',
              false,
              'upperFirst',
              {
                code: this.i18n(
                  `SERVICE_POINT.LABELS.${
                    deliverType === 'EMAIL' ? 'EMAIL' : 'PHONE'
                  }`
                )
              }
            ),
            this.notifyErrorSettings
          );
          break;

        default:
          this.$kit.notify.show(
            this.i18n('SERVICE_POINT.LABELS.ERROR'),
            this.i18n('SERVICE_POINT.ERRORS.UNABLE_TO_VALIDATE'),
            this.notifyErrorSettings
          );
          break;
      }
    } finally {
      this.isLoadingData = false;
    }
  }

  /**
   * Handle go back.
   */
  handleGoback() {
    this.$router.back();
  }

  /**
   * Handle change option.
   */
  @Watch('selectedDeliveryOption')
  handleChangeOption() {
    this.phone = null;
    this.email = null;
    this.identityNumber = null;
  }
}
